/**
 * glanceView — maps normalized SportsData + GlanceMap → GlanceViewModel.
 *
 * CLIENT-SIDE (portable into the extension bundle — ADR-002 §5). No imports
 * from server-only modules. Pure; returns a new object.
 *
 * Glance grammar for sports.score (documented + tested):
 *
 *   primary  : score string with optional result prefix, ≤8 chars:
 *              - "W 3-1"   (5 chars) — win result with score
 *              - "L 1-3"   (5 chars) — loss result with score
 *              - "D 1-1"   (5 chars) — draw result with score
 *              - "3-1"     (3 chars) — score without result prefix
 *              - "W 102-98"(8 chars) — NBA: exactly at the 8-char limit WITH prefix!
 *              - "W 21-17" (7 chars) — football/NFL score
 *              - ""        (0 chars) — no game available
 *
 *              Design decision: when result is known (W/L/D), we prepend it to the
 *              score: "W 3-1". This gives the user the outcome at-a-glance without
 *              having to know which team is "home". Threshold: if "R " + score would
 *              exceed 8 chars (i.e., score.length > 6), we omit the prefix.
 *
 *              Budget analysis:
 *                "W 3-1"    = 5 chars ✓ (soccer, typical)
 *                "W 21-17"  = 7 chars ✓ (NFL, typical)
 *                "W 102-98" = 8 chars ✓ (NBA — exactly at limit; prefix included!)
 *                "W 10-4"   = 6 chars ✓ (baseball, typical)
 *                "1234-56"  = 7 chars ✓ (hypothetical very high score — no prefix)
 *
 *   delta    : opponent and status context, ≤20 chars:
 *              "vs ManCity FT" or "vs Lakers live"
 *              Null when glance.delta is null OR no opponent available.
 *
 *   state    : from result field:
 *              - 'W'    → 'positive' (green accent — win!)
 *              - 'L'    → 'negative' (red accent — loss)
 *              - 'D'    → 'neutral' (draw — no strong signal)
 *              - 'live' status → 'live' (special live state, glance-spec §4.1)
 *              - ''     (no result) → 'neutral'
 *              glance.state null → always 'neutral'.
 *
 * Edge-case rulings:
 *   1. No games (score='') → primary='', delta=null, state='neutral'.
 *   2. High-scoring game (NBA: "102-98") → omit result prefix to stay within 8 chars.
 *   3. Live game (status='live') → state='live' regardless of current score result.
 *   4. Draw result → 'neutral' state (not positive, not negative).
 *
 * D-010 NOTE:
 *   Attribution ("Data from TheSportsDB.com") belongs in tile chrome, not here.
 *   The glance view-model has no attribution field — this is intentional per the
 *   DATA_SHAPE_KEYS design. See the final report flag.
 */

import type { GlanceMap } from 'briefpane/schema';
import type { GlanceState, GlanceViewModel } from 'briefpane/contracts';
import type { SportsData } from './normalize.js';

/** Map SportsData result + status → GlanceState. */
function deriveGlanceState(data: SportsData): GlanceState {
  // Live game takes precedence over result
  if (data.status === 'live') return 'live';
  if (data.result === 'W') return 'positive';
  if (data.result === 'L') return 'negative';
  return 'neutral'; // D (draw), or no result yet
}

/**
 * Format the primary score string for the glance slot (≤8 chars).
 *
 * Design decision: prepend result prefix ("W ", "L ", "D ") when the score
 * string is short enough. For high-scoring games, omit the prefix.
 * Threshold: if `score.length + 3` (for "W " prefix + space) exceeds 8, omit.
 */
function formatPrimaryScore(score: string, result: string): string {
  if (!score) return '';

  // Can we fit the result prefix? "W 102-98" = 9 chars — too long.
  // "W " = 2 chars + score: fits if score.length ≤ 6 (2+6=8 chars max).
  if (result && result !== '' && score.length <= 6) {
    return `${result} ${score}`; // e.g. "W 3-1" or "W 21-17"
  }

  // Otherwise just the score, clipped to 8 chars (central clamp safety net)
  return score.slice(0, 8);
}

/**
 * Format the delta context string (opponent + status), ≤20 chars.
 * e.g. "vs ManCity FT" or "vs Lakers live"
 */
function formatDelta(data: SportsData): string | null {
  if (!data.opponent) return null;

  // Truncate opponent to keep "vs " + opponent + " " + status within 20 chars.
  // "vs " = 3 chars, " " + status ≤ 5 chars → opponent budget = 20 - 3 - 6 = 11 chars.
  const opponentTruncated = data.opponent.slice(0, 11);
  const statusStr = data.clock || data.status;

  if (statusStr) {
    return `vs ${opponentTruncated} ${statusStr}`;
  }
  return `vs ${opponentTruncated}`;
}

/**
 * Map normalized SportsData + the tile's GlanceMap → GlanceViewModel.
 *
 * CLIENT-SIDE. Pure. Returns a new object every call (immutable).
 *
 * @param data   normalized sports metrics (from normalize())
 * @param glance validated GlanceMap from the tile config
 */
export function glanceView(data: SportsData, glance: GlanceMap): GlanceViewModel {
  // --- primary slot ---
  const primary = formatPrimaryScore(data.score, data.result);

  // --- delta slot ---
  let delta: string | null = null;
  if (glance.delta !== null) {
    delta = formatDelta(data);
  }

  // --- glance state ---
  let glanceState: GlanceState = 'neutral';
  if (glance.state !== null) {
    glanceState = deriveGlanceState(data);
  }

  return {
    dataState: 'ok',
    glanceState,
    slots: {
      primary,
      delta,
    },
    label: null, // label comes from the tile config, not the adapter
    fetchedAt: null, // fetchedAt comes from the cache envelope
  };
}
